// src/components/dashboard/DashboardHeader.jsx
// ════════════════════════════════════════════════════════════════════════════
//  ACTUALIZACIÓN: Agrega botón 🔔 Centro de Alertas con badge de no leídas
//  El badge se carga desde el endpoint ligero /api/student/notifications/count
//  para no sobrecargar el header en cada render.
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart2, UserCog, Settings, Calendar, FileClock, Bell, CreditCard, GraduationCap, ClipboardList } from 'lucide-react';
import './DashboardHeader.css';
import { useLatido } from '../../hooks/useLatido';

const formatDate = (dateString) => {
  if (!dateString) return 'Nunca';
  const date = new Date(dateString);
  const rtf = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  const days = Math.round((date - new Date()) / (1000 * 60 * 60 * 24));
  return rtf.format(days, 'day');
};

// ── Hook ligero para el badge count — sincronizado con localStorage de dismissed ─
function useNotificationCount() {
  const [count, setCount] = useState(0);

  const fetchCount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');

      // Leer el set de alertas descartadas por el usuario (misma clave que StudentAlertsTab)
      let dismissedIds = new Set();
      try {
        const raw = localStorage.getItem('nt_dismissed_alerts');
        if (raw) dismissedIds = new Set(JSON.parse(raw));
      } catch { /* silencioso */ }

      // Usar el feed completo para contar exactamente lo que el usuario ve
      const res = await fetch('/api/student/notifications', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const json = await res.json();

      // Descontar las que ya descartó localmente
      const visible = (json.alerts || []).filter(a => !dismissedIds.has(a.id));
      const unread  = visible.filter(a => !a.is_read).length;

      setCount(unread);
    } catch {
      // silencioso — el badge simplemente no aparece
    }
  }, []);

  // El contador de la campana: cada 2 minutos con el panel delante, en
  // silencio si el alumno se fue a otra pestaña (19-09-2026).
  useLatido(async () => { await fetchCount(); return false; },
    { base: 2 * 60 * 1000, techo: 10 * 60 * 1000 });

  useEffect(() => {
    // Refrescar también cuando StudentAlertsTab actualiza el localStorage de dismissed
    const onStorage = (e) => {
      if (e.key === 'nt_dismissed_alerts' || e.key === 'nt_read_alerts') {
        fetchCount();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchCount]);

  return { count, refetch: fetchCount };
}

function DashboardHeader({ summaryData, activeSection, onSectionChange }) {
  const currentDate = new Intl.DateTimeFormat('es-MX', { dateStyle: 'full' }).format(new Date());
  const userAlias = summaryData ? (summaryData.alias || 'Estudiante') : '...';
  const lastLogin = summaryData?.last_login;

  const { count: notifCount } = useNotificationCount();

  /* ── Flechas de scroll horizontal (igual que en Header) ── */
  const navScrollRef = useRef(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);

  const checkArrows = useCallback(() => {
    const el = navScrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 8);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  }, []);

  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    checkArrows();
    el.addEventListener('scroll', checkArrows, { passive: true });
    window.addEventListener('resize', checkArrows);
    return () => {
      el.removeEventListener('scroll', checkArrows);
      window.removeEventListener('resize', checkArrows);
    };
  }, [checkArrows]);

  return (
    <header className="dh-container dp-neumorphic">
      <div className="dh-info-section">
        <h1 className="dh-welcome-message">
          ¡Hola de nuevo, <span className="dh-alias">{userAlias}</span>!
        </h1>
        <div className="dh-date-details">
          <div className="dh-date-item">
            <Calendar size={16} />
            <span>{currentDate}</span>
          </div>
          {lastLogin && (
            <div className="dh-date-item">
              <FileClock size={16} />
              <span>Última visita: {formatDate(lastLogin)}</span>
            </div>
          )}
        </div>
      </div>

      <div className="dh-nav-wrapper">
        {/* Flecha izquierda */}
        <button
          className={`dh-nav-arrow dh-nav-arrow-left${showLeft ? ' dh-arrow-show' : ''}`}
          onClick={() => navScrollRef.current?.scrollBy({ left: -120, behavior: 'smooth' })}
          aria-hidden="true"
          tabIndex={-1}
        >
          <span className="dh-arrow-chevron">‹</span>
        </button>

        <nav className="dh-nav-buttons" ref={navScrollRef}>
          {/* Dashboard */}
          <button
            className={`dh-nav-btn ${activeSection === 'dashboard' ? 'active' : ''}`}
            onClick={() => onSectionChange('dashboard')}
          >
            <BarChart2 size={18} />
            <span>Dashboard</span>
          </button>

          {/* 🔔 NUEVO: Centro de Alertas */}
          <button
            className={`dh-nav-btn ${activeSection === 'alerts' ? 'active' : ''}`}
            onClick={() => onSectionChange('alerts')}
            style={{ position: 'relative' }}
            title="Centro de Alertas"
          >
            <Bell size={18} />
            <span>Alertas</span>
            {notifCount > 0 && (
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                borderRadius: 50,
                background: '#ff5e7e',
                color: '#fff',
                fontSize: '0.6rem',
                fontWeight: 900,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxShadow: '0 0 0 2px var(--card-bg)',
                animation: 'dh-badge-pulse 2s ease infinite',
              }}>
                {notifCount > 99 ? '99+' : notifCount}
              </span>
            )}
          </button>

          {/* Mis Preferencias */}
          <button
            className={`dh-nav-btn ${activeSection === 'preferences' ? 'active' : ''}`}
            onClick={() => onSectionChange('preferences')}
          >
            <UserCog size={18} />
            <span>Perfil</span>
          </button>

          {/* Mis Exámenes — va ANTES de Suscripción: se consulta mucho más. */}
          <button
            className={`dh-nav-btn ${activeSection === 'exams' ? 'active' : ''}`}
            onClick={() => onSectionChange('exams')}
          >
            <ClipboardList size={18} />
            <span>Exámenes</span>
          </button>

          {/* Mi Suscripción */}
          <button
            className={`dh-nav-btn ${activeSection === 'subscription' ? 'active' : ''}`}
            onClick={() => onSectionChange('subscription')}
          >
            <CreditCard size={18} />
            <span>Suscripción</span>
          </button>

          {/* Mi Teacher */}
          <button
            className={`dh-nav-btn ${activeSection === 'teacher_agenda' ? 'active' : ''}`}
            onClick={() => onSectionChange('teacher_agenda')}
          >
            <GraduationCap size={18} />
            <span>Clases</span>
          </button>

          {/* Configuración */}
          <button
            className={`dh-nav-btn ${activeSection === 'settings' ? 'active' : ''}`}
            onClick={() => onSectionChange('settings')}
          >
            <Settings size={18} />
            <span>Config</span>
          </button>
        </nav>

        {/* Flecha derecha */}
        <button
          className={`dh-nav-arrow dh-nav-arrow-right${showRight ? ' dh-arrow-show' : ''}`}
          onClick={() => navScrollRef.current?.scrollBy({ left: 120, behavior: 'smooth' })}
          aria-hidden="true"
          tabIndex={-1}
        >
          <span className="dh-arrow-chevron">›</span>
        </button>
      </div>

      {/* Keyframe para el badge pulse — inyectado inline para no tocar el CSS global */}
      <style>{`
                @keyframes dh-badge-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%       { opacity: .7; transform: scale(1.12); }
                }
            `}</style>
    </header>
  );
}

export default DashboardHeader;